import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  FilePlus, 
  History, 
  Users, 
  FileSpreadsheet, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  User,
  BarChart3,
  Settings as SettingsIcon,
  Bell,
  ExternalLink,
  FileText,
  RefreshCw,
  MailPlus,
  Zap,
  Activity,
  Building2
} from 'lucide-react';
import { Badge } from './ui/badge';

const LOGO_URL = "";

const smeItems = [
  { path: '/dashboard', label: 'SME Dashboard', icon: LayoutDashboard },
  { path: '/new-comparison', label: 'New SME Comparison', icon: FilePlus },
  { path: '/history', label: 'SME History', icon: History },
  { path: '/sme-multisite', label: 'SME Multisite', icon: Building2 },
];

const ciItems = [
  { path: '/ci-dashboard', label: 'C&I Dashboard', icon: LayoutDashboard },
  { path: '/contracts', label: 'C&I Contract Generator', icon: FileText },
  { path: '/email-to-quote', label: 'Email to C&I Generator', icon: MailPlus },
  { path: '/renewals', label: 'Automated C&I Renewal', icon: RefreshCw },
  { path: '/ci-history', label: 'C&I History', icon: History },
];

const toolItems = [
  { path: '/tariff-optimisation', label: 'Tariff Optimisation', icon: Zap },
  { path: '/load-shape', label: 'Load Shape Analyser', icon: Activity },
];

const ratesItems = [
  { path: '/rate-sheets', label: 'SME Rates & Tariffs', icon: FileSpreadsheet },
  { path: '/ci-rate-cards', label: 'C&I Rate Cards', icon: FileSpreadsheet },
];

const adminItems = [
  { path: '/users', label: 'User Management', icon: Users },
  { path: '/leads', label: 'Leads', icon: Users },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/rate-limits', label: 'API Rate Limits', icon: BarChart3 },
  { path: '/settings', label: 'Settings', icon: SettingsIcon },
];

export const Layout = ({ children }) => {
  const { user, logout, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (token) {
      const fetchNotifications = () => {
        fetch(`${API_URL}/api/notifications?authorization=${token}`)
          .then(r => r.json())
          .then(data => { setNotifications(data.notifications || []); setUnreadCount(data.unread_count || 0); })
          .catch(() => {});
      };
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [token, API_URL]);

  const markRead = () => {
    fetch(`${API_URL}/api/notifications/mark-read?authorization=${token}`, { method: 'POST' }).then(() => setUnreadCount(0)).catch(() => {});
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <Link
        to={item.path}
        className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
        data-testid={`nav-${item.path.replace('/', '')}`}
      >
        <Icon className="w-5 h-5" />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform duration-300 lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Logo */}
        <div className="p-6 border-b border-slate-200">
          <Link to="/dashboard" className="block text-xl font-bold text-[#015492]">
            Power Compare
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {/* SME Section */}
          <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">SME</p>
          <div className="space-y-1">
            {smeItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>

          {/* C&I Section */}
          <div className="my-3 mx-4 border-t border-slate-200" />
          <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">C&I</p>
          <div className="space-y-1">
            {ciItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>

          {/* Tools Section */}
          <div className="my-3 mx-4 border-t border-slate-200" />
          <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Tools</p>
          <div className="space-y-1">
            {toolItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>

          {user?.role === 'admin' && (
            <>
              <div className="my-3 mx-4 border-t border-slate-200" />
              <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Rates & Uploads</p>
              <div className="space-y-1">
                {ratesItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
              <div className="my-3 mx-4 border-t border-slate-200" />
              <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Administration</p>
              <div className="space-y-1">
                {adminItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
            </>
          )}
        </nav>

        {/* Customer Portal Link */}
        <div className="px-4 py-1">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-50 hover:text-primary transition-colors" data-testid="nav-customer-portal">
            <ExternalLink className="w-4 h-4" />
            <span>Customer Portal</span>
          </a>
        </div>

        {/* Notifications */}
        <div className="px-4 py-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors relative" onClick={markRead} data-testid="notifications-btn">
                <Bell className="w-5 h-5 text-slate-500" />
                <span className="text-sm text-slate-600">Notifications</span>
                {unreadCount > 0 && <Badge className="bg-red-500 text-white text-xs ml-auto">{unreadCount}</Badge>}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-y-auto">
              {notifications.length > 0 ? notifications.slice(0, 10).map(n => (
                <DropdownMenuItem key={n.id} onClick={() => n.comparison_id && navigate(`/comparison/${n.comparison_id}`)} className="cursor-pointer">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{n.type === 'quote_accepted' ? 'Quote Accepted!' : 'Notification'}</p>
                    <p className="text-xs text-slate-500">{n.customer_name} ({n.report_id})</p>
                    <p className="text-xs text-slate-400">{new Date(n.created_at).toLocaleDateString('en-AU')}</p>
                  </div>
                </DropdownMenuItem>
              )) : <DropdownMenuItem disabled>No notifications</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors" data-testid="user-menu-trigger">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem className="text-slate-600">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-slate-100 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="text-lg font-bold text-[#015492]">Power Compare</span>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
